import moment, { Moment } from 'moment'

import isEmpty from "lodash-es/isEmpty"
import { Location as FirestoreLocation } from '..'
import LocationService from '../../services/LocationServices'
import LocationsService from '../../services/LocationsService'
import LogsService from '../../services/LogsService'
import store from '../../store'
import { transformToCamelCase } from '../../util/helper'
import { ILocaleSettings } from '../location'
import BaseModel from './base'

export type Dict = { [key: string]: any }

export enum LocationStatus {
  PROSPECT = 'prospect',
  ACCOUNT = 'account',
}

export interface ILocationPermissions {
  adwordsReportingEnabled?: boolean
  appointmentsEnabled?: boolean
  attributionsReportingEnabled?: boolean
  agentReportingEnabled?: boolean
  botServiceEnabled?: boolean
  bulkRequestsEnabled?: boolean
  campaignsEnabled?: boolean // will stay false
  contactsEnabled?: boolean // crm
  conversationsEnabled?: boolean // crm
  dashboardStatsEnabled?: boolean
  emailBuilderEnabled?: boolean
  facebookAdsReportingEnabled?: boolean
  facebookMessengerEnabled?: boolean
  formsEnabled?: boolean
  funnelsEnabled?: boolean
  // googleAdsReportingEnabled?: boolean
  gmbCallTrackingEnabled?: boolean
  gmbMessagingEnabled?: boolean
  htmlBuilderEnabled?: boolean
  membershipEnabled?: boolean
  marketingEnabled?: boolean
  // onlineListingsEnabled?: boolean;
  opportunitiesEnabled?: boolean
  phoneCallEnabled?: boolean
  reviewsEnabled?: boolean
  settingsEnabled?: boolean
  smsEmailTemplatesEnabled?: boolean
  surveysEnabled?: boolean
  tagsEnabled?: boolean
  textToPayEnabled?: boolean
  triggersEnabled?: boolean
  triggerLinksEnabled?: boolean
  webChatEnabled?: boolean
  websitesEnabled?: boolean
  workflowsEnabled?: boolean
  socialPlanner?: boolean
  invoiceEnabled?: boolean
  bloggingEnabled?: boolean
  affiliateManagerEnabled?: boolean
  proposalsEnabled?: boolean
  communitiesEnabled?: boolean
  certificatesEnabled?: boolean
  mediaStorageEnabled?: boolean
  goKollabEnabled?: boolean
  quizzesEnabled?: boolean
  qrCodesEnabled?: boolean
  launchpadEnabled?: boolean
}

export interface SMSSettings {
  image?: string
  imageEnabled?: boolean
  message?: string
  afterCheckInHours?: number
  enabled?: boolean
  noShowEnabled?: boolean
  noShowImageEnabled?: boolean
  noShowMessage?: string
  noShowImage?: string
  birthdayEnabled?: boolean
  birthdayImageEnabled?: boolean
  birthdayMessage?: string
  birthdayImage?: string
}

export interface IEmailISVSettings {
  has_lc_email?: boolean
  email_charges?: number
  domain?: string
}
export interface IEmailDomainSettings {
  active?: boolean
}
export interface WidgetSettings {
  bgcolor?: string
  titlecolor?: string
  ratingcolor?: string
  headertext?: string
}

export interface TextWidgetSettings {
  heading?: string
  useEmailField: boolean
  autoCountryCode: boolean
  showPrompt: boolean
  enableRevisitMessage: boolean
  subHeading: string
  legalMsg: string
  promptMsg: string
  successMsg: string
  revisitPromptMsg: string
  thankYouMsg: string
  supportContact: string
  countryCode: string
  promptAvatar: string
  widgetPrimaryColor: string
  agencyName: string
  agencyWebsite: string
  showAgencyBranding: boolean
}

export interface ChatWidgetSettings {
  userId?: string
  intromessage?: string
  botquestion?: string
  enabled?: boolean
}

export interface EmailSettings {
  heading?: string
  message?: string
  positive?: string
  negative?: string
  image?: string
  subject?: string
  afterCheckInHours?: number
  enabled?: boolean
  markInvalidHardBounce?: boolean
}

export interface SocialSettings {
  facebookEnabled?: boolean
  facebookStars?: number
  facebookPostLimit?: number
  twitterEnabled?: boolean
  twitterStars?: number
  twitterPostLimit?: number
  directoriesEnabled?: boolean
  directoriesStars?: number
  directoriesPostLimit?: number
  googleplusEnabled?: boolean
  googleplusStars?: number
  googleplusPostLimit?: number
  searchengineEnabled?: boolean
  searchengineStars?: number
  searchenginePostLimit?: number
}

export interface ClioSettings {
  customField?: string
}

export interface DrChronoSettings {
  officeId?: string
}

export interface AttributionSettings {
  interaction?: string
}

export interface QBOSettings {
  triggerOnInvoice?: boolean
}

export interface ITwilioRebillingSettings {
  enabled?: boolean
  markup?: number
}

export type SAASPRODUCT =
  | '2-way-text-messaging'
  | 'gmb-messaging'
  | 'web-chat'
  | 'reputation-management'
  | 'facebook-messenger'
  | 'gmb-call-tracking'
  | 'missed-call-text-back'
  | 'text-to-pay'
  | 'calendar'
  | 'crm'
  | 'opportunities'
  | 'email-marketing'
  | 'form-builder'
  | 'survey-builder'
  | 'trigger-links'
  | 'html-builder'
  | 'sms-email-templates'
  | 'funnels'
  | 'websites'
  | 'workflow'
  | 'membership'
  | 'all-reports'
  | 'triggers'
  | 'campaigns'
  | 'launchpad'

interface IComplementaryCredits {
  amount: number
  type: 'oneTime' | 'monthly'
}

export interface ISaasSettings {
  saasMode: 'activated' | 'setup_pending' | 'not_activated'
  stripeCustomerId?: string
  twilioRebilling: ITwilioRebillingSettings
  mailgunRebilling: IMailgunRebillingSettings
  complementaryCredits?: IComplementaryCredits
  saasProducts?: SAASPRODUCT[]
  stripePlanDetails?: {
    subscriptionId: string
    productId: string
    priceId: string
  }
  saasPlanId?: string,
  locationUpgradeEnabled?: boolean,
  isSaasV_2?: boolean
}

export interface ISuspendedInfo {
  paused: boolean;
  reason?: string;
}

export interface IMailgunRebillingSettings {
  markup: number
  price: number
  enabled: boolean
}
export interface Settings {
  email: EmailSettings
  sms: SMSSettings
  social: SocialSettings
  clio: ClioSettings
  drchrono: DrChronoSettings
  widget: WidgetSettings
  qbo: QBOSettings
  chatwidget: ChatWidgetSettings
  attribution: AttributionSettings
  textwidget: TextWidgetSettings
  saasSettings: ISaasSettings
}

export interface GoogleMyBusiness {
  id?: string
  name?: string
}

export interface ProspectInfo {
  firstName?: string
  lastName?: string
  email?: string
  snapshotId?: string
}

export interface GMBSettingInfo {
  displayName?: string
  contactName?: string
  contactEmail?: string
  brandName?: string
  gmbNumber?: string
  welcomeMessage?: string
  logoURL?: string
  privacyPolicyUrl?: string
}

export interface GMBMessagingStaus {
  messagingStatus?: boolean
  callingStatus?: boolean
}

export interface ProductStatus extends Dict {
  reviews?: boolean
  listings?: boolean
  conversations?: boolean
  social?: boolean
}

export interface AllowBetaAccess {
  outlook: boolean
  payments: boolean
}
export interface Stripe {
  publishableKey: string
  secretKey: string
}

export interface VoicemailFile {
  url: string
  name: string
}

export interface LaunchpadStatus {
  isChatWidgetIntegrated?: boolean
  isChatWidgetDisconnected?: boolean
  isMobileAppInstalled?: boolean
  isStripeDisconnected?: boolean
  isStripeConnected?: boolean
}

export interface WordpressLocationSettings {
  backupFiles?: [{ [key: string]: any }]
  meta?: { [key: string]: any }
  wpId?: string
  status?: string
  statusMessage?: string
  subDomains?: string[]
  installationType?: string
  domain?: string
  stagingDomain?: string
  locationEnabled?: boolean
  paymentStatus?: string
}

export interface CreatedOrUpdatedBy {
  userId?: string
  source: string
  channel: string
}

export interface Shopify {
  id?: string
  name?: string
  domain?: string
}

export interface NicheOption {
  remindDate?: string,
  niche: string,
  others?: boolean
  userId?: string // userId of the user selecting the option
}

export interface IBusinessInformation {
  type: string;
  industry: string;
  registrationId: string;
  registrationType?: string;
  registrationNumber: string;
  regions: Array<string>;
  businessNotRegistered?: boolean;
}


export interface IAuthorizedRepresentative {
  firstName: string;
  lastName: string;
  email: string;
  title: string;
  position: string;
  phoneNumber: string;
}

export interface ILocation {
  id?: string
  name?: string
  companyId?: string
  address?: string
  city?: string
  state?: string
  email?: string
  phone?: string
  postalCode?: string
  country?: string
  website?: string
  allowDuplicateContact?: boolean
  timezone?: string
  deleted?: boolean
  allowDuplicateOpportunity?: boolean
  showAddEliza?: boolean
  showTwilioSummary?: boolean
  botService?: boolean
  permissions?: ILocationPermissions
  firstShotEnabled?: boolean
  defaultLogicalEliza?: string
  ignoreElizaTag?: string
  sendElizaTag?: string
  dateAdded?: string
  dateUpdated?: string
  fbTokenExpired?: boolean
  gmbTokenExpired?: boolean
  searchTerm?: string
  googlePlacesId?: string
  gmb?: GoogleMyBusiness
  googlePlus?: string
  facebookUrl?: string
  facebookPageId?: string
  facebookAdAccount?: string
  facebookAdsMarkupFee?: string,
  googleAdsMarkupFee?: string,
  facebookPageToken?: string
  facebookPageName?: string
  instagram?: string
  blogRss?: string
  yelp?: string
  foursquare?: string
  pinterest?: string
  linkedIn?: string
  youtube?: string
  twitterUrl?: string
  logoUrl?: string
  settings?: Settings
  yextId?: string
  categories?: string[]
  status?: LocationStatus
  prospectInfo?: ProspectInfo
  gmbSettingInfo?: GMBSettingInfo
  gmbMessagingStatus?: GMBMessagingStaus
  gmbMessagingScopeExist?: boolean
  isGmbDisconnected?: boolean
  productStatus?: ProductStatus
  scanReportId?: string
  socialSettings?: SocialSettings
  clioSettings?: ClioSettings
  drChronoSettings?: DrChronoSettings
  googleAnalyticsAccountId?: string
  googleAnalyticsViewId?: string
  googleAnalyticsWebPropertyId?: string
  googleAdwordsId?: string
  googleAdwordsMCCId?: string
  isCalendarV3On?: boolean
  isUserAvailabilityOn?: boolean
  snapshotsLoaded?: string[]
  apiKey?: string
  stripe?: Stripe
  stripeConnectId?: string
  primaryGoogleConnectionId?: string
  defaultEmailService?: string
  disableContactTimezone?: boolean
  allowFacebookNameMerge?: boolean
  esUpdateStatus?: string
  hipaaCompliance?: boolean
  startDayOfWeek?: number
  language?: string
  use24hFormat?: boolean
  allowBetaAccess?: AllowBetaAccess
  forwardingAddresses?: string
  bccEmails?: string
  forwardToAssignedUser?: boolean
  voicemailFile?: VoicemailFile
  incomingCallTimeout?: number
  facebookIgnoreMessages?: boolean
  instagramPageId?: string
  instagramIgnoreMessages?: boolean
  stripeConnectMode?: string
  testStripeConnectId?: string
  launchpadTooltip?: boolean
  isFbDisconnected?: boolean
  launchpad?: LaunchpadStatus
  lastUpdatedBy?: CreatedOrUpdatedBy
  recentGoogleConnectionId?: string
  depreciatedFeatures?: Dict
  yextReseller?: Dict
  enableMissedCallText?: boolean
  missedCallText?: string
  shopify?: Shopify
  nicheOption?: NicheOption,
  locationDeleteInfo?: ILocationDeleteInfo
  suspendedInfo?: ISuspendedInfo
  legalBusinessName?: string
  businessInformation?: IBusinessInformation
  authorizedRepresentative?: IAuthorizedRepresentative
  syncLegacyCompanyName?:boolean
  snapshotId?:string,
  adCampaignObjectives?: { [key: string]: any }
}

export interface IUnsetFields{
  unsetFields: string[]
}

export interface IBlacklistConfig {
  lockedAll?: boolean;
  lockedFeatures?: string[];
  lockedTriggers?: string[];
  lockedWorkflows?: string[];
  lockedSurveys?: string[];
  lockedForms?: string[];
  lockedCampaigns?: string[];
  lockedBulkActions?: string[];
  pastLockedFeatures?: string[];
  version?: number;
}

export interface ILocationDeleteInfo {
  deletionCloudTaskId?: string;
  deleteScheduledInfo?: IRequestedUserInfo;
  deleteCancelledInfo?: IRequestedUserInfo;
  scheduledDeletionTime?: moment.Moment;
  deleteCompletedOn?: moment.Moment;
  closeTwilioSubAccounts?: boolean;
  locationDeleteStatus?:'scheduled' | 'aborted' | 'deleted'
}

export interface IRequestedUserInfo {
  id?: string;
  username?: string;
  email?: string;
  role?: string;
  type?: string;
  requestedIp?: string;
  requestedOn?: moment.Moment;
}
export default class Location extends BaseModel {
  static requestBodyWhiteList = [
    'phone',
    'googlePlacesId',
    'name',
    'address',
    'city',
    'state',
    'country',
    'postalCode',
    'categories',
    'addressGeo',
    'website',
    'companyId',
    'timezone',
    'status',
    'depreciatedFeatures',
    'prospectInfo',
    'snapshotId',
    'nicheOption'
  ]

  constructor(data?: ILocation) {
    super(Location.requestBodyWhiteList, data)
  }

  async save(): Promise<{ [key: string]: any }> {
    return new Promise(async (resolve, reject) => {
      try {
        let response: { [key: string]: any }
        if (this.newRecord) {
          const selectedData =
            Location.requestBodyWhiteList.length === 0
              ? this.data
              : Location.requestBodyWhiteList.reduce((acc, cv) => {
                  acc[cv] = this.data[cv]
                  return acc
                }, {})

          response = await LocationsService.create(selectedData as ILocation)
        } else {
          response = await  LocationsService.update(
            this.id,
            this.updatedData as ILocation
          )
        }

        if(!isEmpty(this.updatedData.nicheOption)) { // in case the niche gets updated send to ES.
          try {
            await LogsService.dump('account-usage', {locationId: this.id, niche: this.nicheOption?.niche, changed: true})
          } catch (err) {
            console.error(err)
          }
        }

        this.clearUpdatedData() // clear updatedData on every save so that we don't send in the same after object again.
        this.newRecord = false // once saved, clear new record, to avoid calling post API twice.
        resolve(response)
      } catch (error) {
        reject(error)
      }
    })
  }

  static async getById(
    locationId: string,
  ): Promise<Location> {
    const response = await LocationService.read(locationId)
    return new Location(response.location)
  }

  private static colours: string[] = [
    '#5A8770',
    '#B2B7BB',
    '#6FA9AB',
    '#F5AF29',
    '#0088B9',
    '#F18636',
    '#D93A37',
    '#A6B12E',
    '#5C9BBC',
    '#F5888D',
    '#9A89B5',
    '#407887',
    '#9A89B5',
    '#5A8770',
    '#D33F33',
    '#A2B01F',
    '#F0B126',
    '#0087BF',
    '#F18636',
    '#0087BF',
    '#B2B7BB',
    '#72ACAE',
    '#9C8AB4',
    '#5A8770',
    '#EEB424',
    '#407887',
  ]

  get profileColor(): string {
    // const firstCharIndex = (this.firstName || 'A').toUpperCase().charCodeAt(0) - 65;
    // const secondCharIndex = (this.lastName || 'A').toUpperCase().charCodeAt(0) - 65;
    // const colourIndex = Math.round((firstCharIndex + secondCharIndex) / 2) % 25;
    return Location.colours[1]
  }

  // Getters and Setters
  get name(): string {
    return this.data.name
  }

  set name(name: string) {
    this.data.name = name
    this.data.nameLowerCase = name.toLocaleLowerCase()

    this.updatedData.name = name
  }

  get legalBusinessName(): string {
    return this.data.legalBusinessName;
  }

  set legalBusinessName(legalBusinessName: string) {
    this.data.legalBusinessName = legalBusinessName;
    this.updatedData.legalBusinessName = legalBusinessName;
  }

  get companyId(): string {
    return this.data['companyId']
  }

  set companyId(companyId: string) {
    this.data.companyId = companyId
    this.updatedData.companyId = companyId
  }

  get address(): string {
    return this.data.address
  }

  set address(address: string) {
    this.data.address = address
    this.updatedData.address = address
  }

  get addressGeo(): { latitude: number; longitude: number } | undefined {
    return this.data.addressGeo
  }

  set addressGeo(addressGeo: { latitude: number; longitude: number }) {
    this.data.addressGeo = addressGeo
    this.updatedData.addressGeo = addressGeo
  }

  get city(): string {
    return this.data.city
  }

  set city(city: string) {
    this.data.city = city
    this.updatedData.city = city
  }

  get state(): string {
    return this.data.state
  }

  set state(state: string) {
    if(this.state !== state) this.updatedData.state = state
    this.data.state = state
  }

  get email(): string {
    return this.data.email
  }

  set email(email: string) {
    this.data.email = email
    this.updatedData.email = email
  }

  get phone(): string {
    return this.data.phone
  }

  set phone(phone: string) {
    this.data.phone = phone
    this.updatedData.phone = phone
  }

  get postalCode(): string {
    return this.data.postalCode
  }

  set postalCode(postalCode: string) {
    this.data.postalCode = postalCode
    this.updatedData.postalCode = postalCode
  }

  get country(): string {
    return this.data['country']
  }

  set country(country: string) {
    this.data.country = country
    this.updatedData.country = country
  }

  get locale(): string {
    return this.data['locale']
  }

  set locale(locale: string) {
    this.data.locale = locale
    this.updatedData.locale = locale
  }

  get localeSettings(): ILocaleSettings {
    return this.data['localeSettings'] || {}
  }

  set localeSettings(localeSettings: ILocaleSettings) {
    this.data.localeSettings = localeSettings
    this.updatedData.localeSettings = localeSettings
  }

  get allowDuplicateContact(): boolean {
    return this.data.allowDuplicateContact !== undefined
      ? this.data.allowDuplicateContact
      : false
  }

  set allowDuplicateContact(payload: boolean) {
    this.data.allowDuplicateContact = payload
    this.updatedData.allowDuplicateContact = payload
  }

  get timezone(): string {
    return this.data.timezone
  }

  set timezone(timezone: string) {
    this.data.timezone = timezone
    this.updatedData.timezone = timezone
  }

  get deleted(): boolean {
    return this.data.deleted
  }

  set deleted(deleted: boolean) {
    this.data.deleted = deleted
    this.updatedData.deleted = deleted
  }

  get allowDuplicateOpportunity(): boolean {
    return this.data.allowDuplicateOpportunity !== undefined
      ? this.data.allowDuplicateOpportunity
      : false
  }

  set allowDuplicateOpportunity(allowDuplicateOpportunity: boolean) {
    this.data.allowDuplicateOpportunity = allowDuplicateOpportunity
    this.updatedData.allowDuplicateOpportunity = allowDuplicateOpportunity
  }

  get showAddEliza(): boolean {
    return this.data.showAddEliza
  }

  set showAddEliza(showAddEliza: boolean) {
    this.data.showAddEliza = showAddEliza
    this.updatedData.showAddEliza = showAddEliza
  }

  get showTwilioSummary(): boolean {
    return this.data.showTwilioSummary
  }

  set showTwilioSummary(showTwilioSummary: boolean) {
    this.data.showTwilioSummary = showTwilioSummary
    this.updatedData.showTwilioSummary = showTwilioSummary
  }

  get botService(): boolean {
    return this.data.botService
  }

  set botService(botService: boolean) {
    this.data.botService = botService
    this.updatedData.botService = botService
  }

  get permissions(): ILocationPermissions {
    if (!this.data.permissions) {
      const permissions = {} as ILocationPermissions

      permissions.adwordsReportingEnabled = true
      permissions.appointmentsEnabled = true
      permissions.attributionsReportingEnabled = true
      permissions.agentReportingEnabled = true
      permissions.botServiceEnabled = true
      permissions.bulkRequestsEnabled = true
      permissions.campaignsEnabled = true
      permissions.contactsEnabled = true
      permissions.conversationsEnabled = true
      permissions.dashboardStatsEnabled = true
      permissions.emailBuilderEnabled = true
      permissions.facebookAdsReportingEnabled = true
      permissions.facebookMessengerEnabled = true
      permissions.formsEnabled = true
      permissions.funnelsEnabled = true
      // permissions.googleAdsReportingEnabled = true
      permissions.gmbCallTrackingEnabled = true
      permissions.gmbMessagingEnabled = true
      permissions.htmlBuilderEnabled = true
      permissions.membershipEnabled = true
      permissions.marketingEnabled = true
      permissions.opportunitiesEnabled = true
      permissions.phoneCallEnabled = true
      permissions.reviewsEnabled = true
      permissions.settingsEnabled = true
      permissions.smsEmailTemplatesEnabled = true
      permissions.surveysEnabled = true
      permissions.textToPayEnabled = true
      permissions.tagsEnabled = true
      permissions.triggersEnabled = true
      permissions.triggerLinksEnabled = true
      permissions.webChatEnabled = true
      permissions.websitesEnabled = true
      permissions.workflowsEnabled = true
      permissions.bloggingEnabled = true
      permissions.affiliateManagerEnabled = true
      permissions.proposalsEnabled = true
      permissions.communitiesEnabled = true
      permissions.certificatesEnabled = true
      permissions.mediaStorageEnabled = true
      permissions.goKollabEnabled = true
      permissions.quizzesEnabled = true
      permissions.qrCodesEnabled = true
      permissions.launchpadEnabled = true

      this.data.permissions = permissions
    }

    return this.data.permissions
  }

  set permissions(permissions: ILocationPermissions) {
    this.data.permissions = permissions
    this.updatedData.permissions = permissions
  }

  get firstShotEnabled(): boolean {
    return this.data.firstShotEnabled
  }

  set firstShotEnabled(firstShotEnabled: boolean) {
    this.data.firstShotEnabled = firstShotEnabled
    this.updatedData.firstShotEnabled = firstShotEnabled
  }

  get defaultLogicalEliza(): string {
    return this.data.defaultLogicalEliza
  }

  set defaultLogicalEliza(defaultLogicalEliza: string) {
    this.data.defaultLogicalEliza = defaultLogicalEliza
    this.updatedData.defaultLogicalEliza = defaultLogicalEliza
  }

  get ignoreElizaTag(): string {
    return this.data.ignoreElizaTag
  }

  set ignoreElizaTag(ignoreElizaTag: string) {
    this.data.ignoreElizaTag = ignoreElizaTag
    this.updatedData.ignoreElizaTag = ignoreElizaTag
  }

  get sendElizaTag(): string {
    return this.data.sendElizaTag
  }

  set sendElizaTag(sendElizaTag: string) {
    this.data.sendElizaTag = sendElizaTag
    this.updatedData.sendElizaTag = sendElizaTag
  }

  get fbTokenExpired(): boolean {
    return this.data.fbTokenExpired
  }

  set fbTokenExpired(fbTokenExpired: boolean) {
    this.data.fbTokenExpired = fbTokenExpired
    this.updatedData.fbTokenExpired = fbTokenExpired
  }

  get gmbTokenExpired(): boolean {
    return this.data.gmbTokenExpired
  }

  set gmbTokenExpired(gmbTokenExpired: boolean) {
    this.data.gmbTokenExpired = gmbTokenExpired
    this.updatedData.gmbTokenExpired = gmbTokenExpired
  }

  get website(): string {
    return this.data.website
  }

  set website(website: string) {
    this.data.website = website
    this.updatedData.website = website
  }

  get searchTerm(): string {
    return this.data.searchTerm
  }

  set searchTerm(searchTerm: string) {
    this.data.searchTerm = searchTerm
    this.updatedData.searchTerm = searchTerm
  }

  get facebookUrl(): string {
    return this.data.facebookUrl
  }

  set facebookUrl(facebookUrl: string) {
    this.data.facebookUrl = facebookUrl
    this.updatedData.facebookUrl = facebookUrl
  }

  get facebookPageId(): string {
    return this.data.facebookPageId
  }

  get emailIsvSettings(): IEmailISVSettings | object {
    return this.data?.emailIsvSettings ?? {}
  }

  get emailDomains(): { [key: string]: IEmailDomainSettings } | object {
    return this.data?.emailDomains ?? {}
  }

  set facebookPageId(facebookPageId: string) {
    this.data.facebookPageId = facebookPageId
    this.updatedData.facebookPageId = facebookPageId
  }

  get facebookAdAccount(): string {
    return this.data.facebookAdAccount
  }

  set facebookAdAccount(facebookAdAccount: string) {
    this.data.facebookAdAccount = facebookAdAccount
    this.updatedData.facebookAdAccount = facebookAdAccount
  }

  get linkedInPageId(): string {
    return this.data.linkedinPageId
  }

  set linkedInPageId(linkedInPageId: string) {
    this.data.linkedinPageId = linkedInPageId
    this.updatedData.linkedinPageId = linkedInPageId
  }

  set googlePlacesId(googlePlacesId: string) {
    this.data.googlePlacesId = googlePlacesId
    this.updatedData.googlePlacesId = googlePlacesId
  }

  get googlePlacesId(): string {
    return this.data.googlePlacesId
  }

  get gmb(): GoogleMyBusiness {
    if (!this.data.gmb) this.data.gmb = {}
    return this.data.gmb
  }

  get facebookPageToken(): string {
    return this.data.facebookPageToken
  }

  set facebookPageToken(facebookPageToken: string) {
    this.data.facebookPageToken = facebookPageToken
    this.updatedData.facebookPageToken = facebookPageToken
  }

  get facebookPageName(): string {
    return this.data.facebookPageName
  }

  set facebookPageName(facebookPageName: string) {
    this.data.facebookPageName = facebookPageName
    this.updatedData.facebookPageName = facebookPageName
  }

  get googlePlus(): string {
    return this.data.googlePlus
  }

  set googlePlus(googlePlus: string) {
    this.data.googlePlus = googlePlus
    this.updatedData.googlePlus = googlePlus
  }

  get twitter(): string {
    return this.data.twitter
  }

  set twitter(twitter: string) {
    this.data.twitter = twitter
    this.updatedData.twitter = twitter
  }

  get linkedIn(): string {
    return this.data.linkedIn
  }

  set linkedIn(linkedIn: string) {
    this.data.linkedIn = linkedIn
    this.updatedData.linkedIn = linkedIn
  }

  get pinterest(): string {
    return this.data.pinterest
  }

  set pinterest(pinterest: string) {
    this.data.pinterest = pinterest
    this.updatedData.pinterest = pinterest
  }

  get instagram(): string {
    return this.data.instagram
  }

  set instagram(instagram: string) {
    this.data.instagram = instagram
    this.updatedData.instagram = instagram
  }

  get youtube(): string {
    return this.data.youtube
  }

  set youtube(youtube: string) {
    this.data.youtube = youtube
    this.updatedData.youtube = youtube
  }

  get blogRss(): string {
    return this.data.blogRss
  }

  set blogRss(blogRss: string) {
    this.data.blogRss = blogRss
    this.updatedData.blogRss = blogRss
  }

  get yelp(): string {
    return this.data.yelp
  }

  set yelp(yelp: string) {
    this.data.yelp = yelp
    this.updatedData.yelp = yelp
  }

  get foursquare(): string {
    return this.data.foursquare
  }

  set foursquare(foursquare: string) {
    this.data.foursquare = foursquare
    this.updatedData.foursquare = foursquare
  }

  get logoUrl(): string {
    return this.data.logoUrl
  }

  set logoUrl(logoUrl: string) {
    this.data.logoUrl = logoUrl
    this.updatedData.logoUrl = logoUrl
  }

  get settings(): Settings {
    if (!this.data.settings) this.data.settings = {}
    if (!this.data.settings.sms) this.data.settings.sms = {}
    if (!this.data.settings.email) this.data.settings.email = {}
    if (!this.data.settings.drchrono) this.data.settings.drchrono = {}
    if (!this.data.settings.social) this.data.settings.social = {}
    if (!this.data.settings.clio) this.data.settings.clio = {}
    if (!this.data.settings.widget) this.data.settings.widget = {}
    if (!this.data.settings.qbo) this.data.settings.qbo = {}
    if (!this.data.settings.chatwidget) this.data.settings.chatwidget = {}
    if (!this.data.settings.attribution) this.data.settings.attribution = {}
    if (!this.data.settings.textwidget) this.data.settings.textwidget = {}
    if (!this.data.settings.saasSettings) {
      const twilioRebilling = {
        enabled: false,
        markup: 10,
      } as ITwilioRebillingSettings

      this.data.settings.saasSettings = {
        saasMode: 'not_activated',
        twilioRebilling: twilioRebilling,
      } as ISaasSettings
    }

    return this.data.settings
  }

  set settings(settings: Settings) {
    this.data.settings = settings
    this.updatedData.settings = settings
  }

  get suspendedInfo() {
    return this.data.suspendedInfo;
  }

  set suspendedInfo(suspendedInfo: ISuspendedInfo) {
    this.data.suspendedInfo = suspendedInfo;
  }

  get locationDeleteInfo(): ILocationDeleteInfo {
    if (!this.data.locationDeleteInfo) this.data.locationDeleteInfo = {};
    if (!this.data.locationDeleteInfo.deleteScheduledInfo) this.data.locationDeleteInfo.deleteScheduledInfo = {};
    if (!this.data.locationDeleteInfo.deleteCancelledInfo) this.data.locationDeleteInfo.deleteCancelledInfo = {};
    if (!this.data.locationDeleteInfo.closeTwilioSubAccounts) this.data.locationDeleteInfo.closeTwilioSubAccounts = false;
    if (!this.data.locationDeleteInfo.locationDeleteStatus) this.data.locationDeleteInfo.locationDeleteStatus = 'aborted';
    return this.data.locationDeleteInfo;
  }

  set locationDeleteInfo(deleteInfo: ILocationDeleteInfo) {
    if (!this.data.locationDeleteInfo) {
      this.data.locationDeleteInfo = {};
    }
    if (deleteInfo.deleteScheduledInfo) {
      this.data.locationDeleteInfo.deleteScheduledInfo = deleteInfo.deleteScheduledInfo;
    }
    if (deleteInfo.deleteCancelledInfo) {
      this.data.locationDeleteInfo.deleteCancelledInfo = deleteInfo.deleteCancelledInfo;
    }
    if (deleteInfo.deleteCompletedOn) {
      this.data.locationDeleteInfo.deleteCompletedOn = deleteInfo.deleteCompletedOn;
    }
    if (deleteInfo.closeTwilioSubAccounts) {
      this.data.locationDeleteInfo.closeTwilioSubAccounts = deleteInfo.closeTwilioSubAccounts;
    }
    if (deleteInfo.deletionCloudTaskId) {
      this.data.locationDeleteInfo.deletionCloudTaskId = deleteInfo.deletionCloudTaskId;
    }
    if (deleteInfo.scheduledDeletionTime) {
      this.data.locationDeleteInfo.scheduledDeletionTime = deleteInfo.scheduledDeletionTime;
    }
    if (deleteInfo.locationDeleteStatus) {
      this.data.locationDeleteInfo.locationDeleteStatus = deleteInfo.locationDeleteStatus;
    }
    this.updatedData.locationDeleteInfo = this.data.locationDeleteInfo;
  }

  get yextId(): string {
    return this.data.yextId
  }

  set yextId(yextId: string) {
    this.data.yextId = yextId
    this.updatedData.yextId = yextId
  }

  get categories(): string[] {
    return this.data.categories
  }

  set categories(categories: string[]) {
    this.data.categories = categories
    this.updatedData.categories = categories
  }

  get status(): string {
    return this.data.status
  }

  set status(status: string) {
    this.data.status = status
    this.updatedData.status = status
  }

  get prospectInfo(): ProspectInfo {
    if (!this.data.prospectInfo)
      this.data.prospectInfo = { email: '', firstName: '', lastName: '', snapshotId: null }
    return this.data.prospectInfo
  }

  set prospectInfo(prospectInfo: ProspectInfo) {
    this.data.prospectInfo = prospectInfo
    this.updatedData.prospectInfo = prospectInfo
  }

  get gmbSettingInfo(): GMBSettingInfo {
    return this.data.gmbSettingInfo
  }

  set gmbSettingInfo(GMBSettingInfo: GMBSettingInfo) {
    this.data.gmbSettingInfo = GMBSettingInfo
    this.updatedData.gmbSettingInfo = GMBSettingInfo
  }

  get gmbMessagingStatus(): GMBMessagingStaus {
    return this.data.gmbMessagingStatus
  }

  set gmbMessagingStatus(gmbMessagingStaus: GMBMessagingStaus) {
    this.data.gmbMessagingStatus = gmbMessagingStaus
    this.updatedData.gmbMessagingStatus = gmbMessagingStaus
  }

  get gmbMessagingScopeExist(): boolean {
    return this.data.gmbBusinessScopeExist
  }

  set gmbMessagingScopeExist(gmbMessagingScopeExist: boolean) {
    this.data.gmbBusinessScopeExist = gmbMessagingScopeExist
    this.updatedData.gmbBusinessScopeExist = gmbMessagingScopeExist
  }

  get productStatus(): ProductStatus {
    return this.data.productStatus
  }

  set productStatus(productStatus: ProductStatus) {
    this.data.productStatus = productStatus
    this.updatedData.productStatus = productStatus
  }

  public async convertToAccount() {
    this.status = LocationStatus.ACCOUNT
    await this.save()
  }

  get twitterUrl(): string {
    return this.data.twitterUrl
  }

  set twitterUrl(twitterUrl: string) {
    this.data.twitterUrl = twitterUrl
    this.updatedData.twitterUrl = twitterUrl
  }

  get scanReportId(): string {
    return this.data.scanReportId
  }

  set scanReportId(scanReportId: string) {
    this.data.scanReportId = scanReportId
    this.updatedData.scanReportId = scanReportId
  }

  get socialSettings(): { [key: string]: any } {
    if (!this.settings.social) this.settings.social = {}
    return this.settings.social
  }

  get clioSettings(): { [key: string]: any } {
    if (!this.settings.clio) this.settings.clio = {}
    return this.settings.clio
  }

  get drChronoSettings(): { [key: string]: any } {
    if (!this.settings.drchrono) this.settings.drchrono = {}
    return this.settings.drchrono
  }

  get googleAnalyticsAccountId(): string {
    return this.data.googleAnalyticsAccountId
  }

  set googleAnalyticsAccountId(googleAnalyticsAccountId: string) {
    this.data.googleAnalyticsAccountId = googleAnalyticsAccountId
    this.updatedData.googleAnalyticsAccountId = googleAnalyticsAccountId
  }

  get googleAnalyticsViewId(): string {
    return this.data.googleAnalyticsViewId
  }

  set googleAnalyticsViewId(googleAnalyticsViewId: string) {
    this.data.googleAnalyticsViewId = googleAnalyticsViewId
    this.updatedData.googleAnalyticsViewId = googleAnalyticsViewId
  }

  get googleAnalyticsWebPropertyId(): string {
    return this.data.googleAnalyticsWebPropertyId
  }

  set googleAnalyticsWebPropertyId(googleAnalyticsWebPropertyId: string) {
    this.data.googleAnalyticsWebPropertyId = googleAnalyticsWebPropertyId
    this.updatedData.googleAnalyticsWebPropertyId = googleAnalyticsWebPropertyId
  }

  get googleAdwordsId(): string {
    return this.data.googleAdwordsId
  }

  set googleAdwordsId(googleAdwordsId: string) {
    this.data.googleAdwordsId = googleAdwordsId
    this.updatedData.googleAdwordsId = googleAdwordsId
  }

  get googleAdwordsMCCId(): string {
    return this.data.googleAdwordsMccId
  }

  set googleAdwordsMCCId(googleAdwordsCCId: string) {
    this.data.googleAdwordsMccId = googleAdwordsCCId
    this.updatedData.googleAdwordsMccId = googleAdwordsCCId
  }

  /**
   * Whether new calendar feature (Calendar v3) is turned on or not
   */
  get isCalendarV3On(): boolean {
    return this.data.isCalendarV3On
  }

  set isCalendarV3On(value: boolean) {
    this.data.isCalendarV3On = value
    this.updatedData.isCalendarV3On = value
  }

  /**
   * Whether UserAvailability feature is turned on or not.
   * Allow location users to set their own availability from their profile page. This really helps when calendar service team members (users) are working in different time zones globally. You can call this Global Availability.
   */
  get isUserAvailabilityOn(): boolean {
    return this.data.isUserAvailabilityOn
  }

  set isUserAvailabilityOn(value: boolean) {
    this.data.isUserAvailabilityOn = value
    this.updatedData.isUserAvailabilityOn = value
  }

  get snapshotsLoaded(): string[] {
    return this.data['snapshotsLoaded']
  }

  get formatedName() {
    let name = this.name
    if (this.address || this.city || this.state) name += ' -- '
    if (this.address) name += this.address
    if (this.address && this.city) name += ', '
    if (this.city) name += this.city
    if (this.city && this.state) name += ', '
    if (this.state) name += this.state
    return name
  }

  get fullAddressLine(): string {
    let address = ''
    if (this.address) {
      address += this.address + ' '
    }
    if (this.city) {
      address += this.city + ' '
    }
    if (this.state) {
      address += this.state + ' '
    }
    if (this.postalCode) {
      address += this.postalCode
    }
    return address
  }

  get stripe(): Stripe {
    return this.data.stripe
  }

  set stripe(stripe: Stripe) {
    this.data.stripe = stripe
    this.updatedData.stripe = stripe
  }

  get stripeConnectId(): string {
    return this.data['stripeConnectId']
  }

  set stripeConnectId(stripeConnectId: string) {
    this.data['stripeConnectId'] = stripeConnectId
    this.updatedData['stripeConnectId'] = stripeConnectId
  }

  get primaryGoogleConnectionId(): string {
    return this.data.primaryGoogleConnectionId
  }

  set primaryGoogleConnectionId(primaryGoogleConnectionId: string) {
    this.data.primaryGoogleConnectionId = primaryGoogleConnectionId
    this.updatedData.primaryGoogleConnectionId = primaryGoogleConnectionId
  }

  get defaultEmailService(): string {
    return this.data.defaultEmailService
  }

  set defaultEmailService(defaultEmailService: string) {
    this.data.defaultEmailService = defaultEmailService
    this.updatedData.defaultEmailService = defaultEmailService
  }

  get disableContactTimezone(): boolean {
    return this.data.disableContactTimezone !== undefined
      ? this.data.disableContactTimezone
      : false
  }

  set disableContactTimezone(disableContactTimezone: boolean) {
    this.data.disableContactTimezone = disableContactTimezone
    this.updatedData.disableContactTimezone = disableContactTimezone
  }

  // get validatePhoneNumbers(): boolean {
  //   return this.data.validatePhoneNumbers !== undefined
  //     ? this.data.validatePhoneNumbers
  //     : false
  // }

  // set validatePhoneNumbers(validatePhoneNumbers: boolean) {
  //   this.data.validatePhoneNumbers = validatePhoneNumbers
  // }

  get allowFacebookNameMerge(): boolean {
    return this.data.allowFacebookNameMerge !== undefined
      ? this.data.allowFacebookNameMerge
      : false
  }

  set allowFacebookNameMerge(allowFacebookNameMerge: boolean) {
    this.data.allowFacebookNameMerge = allowFacebookNameMerge
    this.updatedData.allowFacebookNameMerge = allowFacebookNameMerge
  }

  get esUpdateStatus(): string {
    return this.data.esUpdateStatus
  }

  set esUpdateStatus(val: string) {
    this.data.esUpdateStatus = val
    this.updatedData.esUpdateStatus = val
  }

  get hipaaCompliance(): boolean {
    return this.data['hipaaCompliance'] || false
  }

  set hipaaCompliance(hipaaCompliance: boolean) {
    this.data['hipaaCompliance'] = hipaaCompliance || false
    this.updatedData['hipaaCompliance'] = hipaaCompliance || false
  }

  get validateEmails(): boolean {
    return this.data?.validateEmails ?? false
  }

  set validateEmails(value: boolean) {
    this.data.validateEmails = value;
  }

  get reverifyEmailOption(): boolean {
    return this.data?.reverifyEmailOption ?? true
  }

  set reverifyEmailOption(value: boolean) {
    this.data.reverifyEmailOption = value;
  }

  get hasLcEmail(): boolean {
    return this.data?.hasLcEmail ?? (this.data?.settings?.emailIsvSettings?.hasLcEmail || false)
  }

  set hasLcEmail(value: boolean) {
    this.data.hasLcEmail = value;
  }

  get blockAddSmtp(): boolean {
    return this.data?.blockAddSmtp ?? false
  }

  set blockAddSmtp(value: boolean) {
    this.data.blockAddSmtp = value;
  }

  get startDayOfWeek(): number {
    if (this.data.startDayOfWeek === undefined) return 0
    return this.data.startDayOfWeek
  }

  set startDayOfWeek(value: number) {
    this.data.startDayOfWeek = value
    this.updatedData.startDayOfWeek = value
  }

  get language(): string {
    return this.data.language || '' // Default to '' which is English
  }

  set language(val: string) {
    this.data.language = val
    this.updatedData.language = val
  }

  get use24hFormat(): boolean {
    return this.data.use24hFormat || false
  }

  set use24hFormat(use24hFormat: boolean) {
    this.data.use24hFormat = use24hFormat || false
    this.updatedData.use24hFormat = use24hFormat || false
  }

  get allowBetaAccess(): AllowBetaAccess {
    return this.data.allowBetaAccess
  }

  set allowBetaAccess(allowBetaAccess: AllowBetaAccess) {
    this.data.allowBetaAccess = allowBetaAccess
    this.updatedData.allowBetaAccess = allowBetaAccess
  }

  public delete() {
    // @todo
  }

  get forwardingAddresses(): string {
    return this.data.forwardingAddresses
  }

  set forwardingAddresses(forwardingAddresses: string) {
    this.data.forwardingAddresses = forwardingAddresses
    this.updatedData.forwardingAddresses = forwardingAddresses
  }

  get bccEmails(): string {
    return this.data.bccEmails
  }

  set bccEmails(bccEmails: string) {
    this.data.bccEmails = bccEmails
    this.updatedData.bccEmails = bccEmails
  }

  get forwardToAssignedUser(): boolean {
    return this.data.forwardToAssignedUser
  }

  set forwardToAssignedUser(forwardToAssignedUser: boolean) {
    this.data.forwardToAssignedUser = forwardToAssignedUser
    this.updatedData.forwardToAssignedUser = forwardToAssignedUser
  }

  get voicemailFile(): VoicemailFile {
    return this.data.voicemailFile || {}
  }

  set voicemailFile(voicemailFile: VoicemailFile) {
    this.data.voicemailFile = voicemailFile
    this.updatedData.voicemailFile = voicemailFile
  }

  get incomingCallTimeout(): number {
    return this.data.incomingCallTimeout
  }

  set incomingCallTimeout(incomingCallTimeout: number) {
    this.data.incomingCallTimeout = incomingCallTimeout
    this.updatedData.incomingCallTimeout = incomingCallTimeout
  }

  get facebookIgnoreMessages(): boolean {
    return this.data.facebookIgnoreMessages
  }

  set facebookIgnoreMessages(facebookIgnoreMessages: boolean) {
    this.data.facebookIgnoreMessages = facebookIgnoreMessages
    this.updatedData.facebookIgnoreMessages = facebookIgnoreMessages
  }

  get instagramPageId(): string {
    return this.data.instagramPageId
  }

  set instagramPageId(instagramPageId: string) {
    this.data.instagramPageId = instagramPageId
    this.updatedData.instagramPageId = instagramPageId
  }

  get instagramIgnoreMessages(): boolean {
    return this.data.instagramIgnoreMessages
  }

  set instagramIgnoreMessages(instagramIgnoreMessages: boolean) {
    this.data.instagramIgnoreMessages = instagramIgnoreMessages
    this.updatedData.instagramIgnoreMessages = instagramIgnoreMessages
  }

  get stripeConnectMode(): string {
    return this.data.stripeConnectMode || 'live'
  }

  set stripeConnectMode(stripeConnectMode: string) {
    this.data.stripeConnectMode = stripeConnectMode
    this.updatedData.stripeConnectMode = stripeConnectMode
  }

  get testStripeConnectId(): string {
    return this.data['testStripeConnectId']
  }

  set testStripeConnectId(testStripeConnectId: string) {
    this.data['testStripeConnectId'] = testStripeConnectId
    this.updatedData['testStripeConnectId'] = testStripeConnectId
  }

  get launchpadTooltip(): boolean {
    return this.data.launchpadTooltip
  }

  set launchpadTooltip(launchpadTooltip: boolean) {
    this.data.launchpadTooltip = launchpadTooltip
    this.updatedData.launchpadTooltip = launchpadTooltip
  }

  get isGmbDisconnected(): boolean {
    return this.data.isGmbDisconnected
  }

  set isGmbDisconnected(isGmbDisconnected: boolean) {
    this.data.isGmbDisconnected = isGmbDisconnected
    this.updatedData.isGmbDisconnected = isGmbDisconnected
  }

  get isFbDisconnected(): boolean {
    return this.data.isFbDisconnected
  }

  set isFbDisconnected(isFbDisconnected: boolean) {
    this.data.isFbDisconnected = isFbDisconnected
    this.updatedData.isFbDisconnected = isFbDisconnected
  }

  get launchpad(): LaunchpadStatus {
    return this.data.launchpad || {}
  }

  set launchpad(launchpad: LaunchpadStatus) {
    this.data.launchpad = launchpad
    this.updatedData.launchpad = launchpad
  }

  get lastUpdatedBy(): CreatedOrUpdatedBy {
    return this.data.lastUpdatedBy
  }

  set lastUpdatedBy(value: CreatedOrUpdatedBy) {
    this.data.lastUpdatedBy = value
    this.updatedData.lastUpdatedBy = value
  }

  get recentGoogleConnectionId(): string | undefined {
    return this.data.recentGoogleConnectionId
  }

  set recentGoogleConnectionId(recentGoogleConnectionId: string | undefined) {
    this.data.recentGoogleConnectionId = recentGoogleConnectionId
    this.updatedData.recentGoogleConnectionId = recentGoogleConnectionId
  }

  get depreciatedFeatures(): { [key: string]: any }[] {
    return this.data.depreciatedFeatures ? this.data.depreciatedFeatures : []
  }

  set depreciatedFeatures(features: { [key: string]: any }[]) {
    this.data.depreciatedFeatures = features
    this.updatedData.depreciatedFeatures = features
  }

  get yextReseller(): object {
    if (!this.data.reseller) {
      this.data.reseller = {}
    }
    if (this.data.reseller.yext) {
      return this.data.reseller.yext
    }
    this.data.reseller.yext = { locationEnabled: true }
    return this.data.reseller.yext
  }

  set yextReseller(yextSetting: { [key: string]: any }) {
    if (!this.data.reseller) {
      this.data.reseller = {}
    }
    this.data.reseller.yext = { ...yextSetting, hlPrice: 30 } // hlPrice:30 is important because client and try to override although we are only using it display purpose
    this.updatedData.yextReseller = { ...yextSetting, hlPrice: 30 } // instead of sending entire reseller, just send wordpress reseller, and the rest is handled in the backend.
  }

  get enableMissedCallText(): boolean {
    if (
      moment('2021-08-13').isSameOrBefore(moment.unix(this.dateAdded?.seconds))
    ) {
      return this.data.enableMissedCallText !== undefined
        ? this.data.enableMissedCallText
        : true
    }
    return this.data.enableMissedCallText !== undefined
      ? this.data.enableMissedCallText
      : false
  }

  set enableMissedCallText(enableMissedCallText: boolean) {
    this.data.enableMissedCallText = enableMissedCallText
    this.updatedData.enableMissedCallText = enableMissedCallText
  }

  get missedCallText(): string {
    return (
      this.data.missedCallText ||
      'Hi this is {{location.name}}, I saw that we just missed your call how can I help?'
    )
  }

  set missedCallText(missedCallText: string) {
    this.data.missedCallText = missedCallText
    this.updatedData.missedCallText = missedCallText
  }

  get shopify(): Shopify {
    return this.data.shopify
  }

  set shopify(shopify: Shopify) {
    this.data.shopify = shopify
    this.updatedData.shopify = shopify
  }

  set emailSettings(data: EmailSettings) {
    this.data.settings.email = data
    this.updatedData.emailSettings = data
  }

  set smsSettings(data: SMSSettings) {
    this.data.settings.sms = data
    this.updatedData.smsSettings = data
  }

  get nicheOption(): NicheOption {
    return this.data.nicheOption
  }

  set nicheOption(payload: NicheOption) {
    this.data.nicheOption = payload
    this.updatedData.nicheOption = payload
  }
  get businessName(){
    return this.data.businessName
  }
  set businessName(name :string){
     this.data.businessName = name
  }
  get businessInformation(): IBusinessInformation {
    if (!this.data.businessInformation) this.data.businessInformation = {};
    if (!this.data.businessInformation.type) this.data.businessInformation.type = '';
    if (!this.data.businessInformation.industry) this.data.businessInformation.industry = '';
    if (!this.data.businessInformation.registrationId) this.data.businessInformation.registrationId = '';
    if (!this.data.businessInformation.registrationType) this.data.businessInformation.registrationType = '';
    if (!this.data.businessInformation.registrationNumber) this.data.businessInformation.registrationNumber = '';
    if (!this.data.businessInformation.regions) this.data.businessInformation.regions = [];
    if (!this.data.businessInformation.businessNotRegistered) this.data.businessInformation.businessNotRegistered = false;
    return this.data.businessInformation;
  }


  set businessInformation(payload: IBusinessInformation) {
    if (!this.data.businessInformation) {
      this.data.businessInformation = {};
    }
    if (payload.type) {
      this.data.businessInformation.type = payload.type;
    }
    if (payload.industry) {
      this.data.businessInformation.industry = payload.industry;
    }
    if (payload.registrationId) {
      this.data.businessInformation.registrationId = payload.registrationId;
    }
    if (payload.registrationType) {
      this.data.businessInformation.registrationType = payload.registrationType;
    }
    if (payload.registrationNumber) {
      this.data.businessInformation.registrationNumber = payload.registrationNumber;
    }
    if (payload.regions) {
      this.data.businessInformation.regions = payload.regions;
    }
    if (payload.businessNotRegistered) {
      this.data.businessInformation.businessNotRegistered = payload.businessNotRegistered;
    }
    this.updatedData.businessInformation = this.data.businessInformation;
  }



  get authorizedRepresentative(): IAuthorizedRepresentative {
    if (!this.data.authorizedRepresentative) this.data.authorizedRepresentative = {};
    if (!this.data.authorizedRepresentative.firstName) this.data.authorizedRepresentative.firstName = '';
    if (!this.data.authorizedRepresentative.lastName) this.data.authorizedRepresentative.lastName = '';
    if (!this.data.authorizedRepresentative.email) this.data.authorizedRepresentative.email = '';
    if (!this.data.authorizedRepresentative.title) this.data.authorizedRepresentative.title = '';
    if (!this.data.authorizedRepresentative.position) this.data.authorizedRepresentative.position = '';
    if (!this.data.authorizedRepresentative.phoneNumber) this.data.authorizedRepresentative.phoneNumber = '';
    return this.data.authorizedRepresentative;
  }

  set authorizedRepresentative(authorizedRepresentative: IAuthorizedRepresentative) {
    if (!this.data.authorizedRepresentative) {
      this.data.authorizedRepresentative = {};
    }
    if (authorizedRepresentative.firstName) {
      this.data.authorizedRepresentative.firstName = authorizedRepresentative.firstName;
    }
    if (authorizedRepresentative.lastName) {
      this.data.authorizedRepresentative.lastName = authorizedRepresentative.lastName;
    }
    if (authorizedRepresentative.email) {
      this.data.authorizedRepresentative.email = authorizedRepresentative.email;
    }
    if (authorizedRepresentative.title) {
      this.data.authorizedRepresentative.title = authorizedRepresentative.title;
    }
    if (authorizedRepresentative.position) {
      this.data.authorizedRepresentative.position = authorizedRepresentative.position;
    }
    if (authorizedRepresentative.phoneNumber) {
      this.data.authorizedRepresentative.phoneNumber = authorizedRepresentative.phoneNumber;
    }
    this.updatedData.authorizedRepresentative = this.data.authorizedRepresentative;
  }

  get showNichePicker() : boolean {
    // show only for locations created at least 60 days before
    if(moment(this.dateAdded).isBefore(moment().subtract(60, 'days'))) {

      if(!this.nicheOption) return true

      if(this.nicheOption.niche && this.nicheOption.niche.length > 0) return false

      if(!this.nicheOption.niche && !this.nicheOption.remindDate) return true

      if(store.state.user.user?.id !== this.nicheOption.userId) return true

      const now = moment()
      let remindDate: Moment;

      if(this.nicheOption.remindDate) {
        remindDate = moment(this.nicheOption.remindDate)
      }

      if(remindDate && now.isAfter(remindDate)) {
        return true
      }
    }

    return false
  }

  get niche(): string {
    if(this.nicheOption?.others) return 'others'
    return this.nicheOption?.niche || ''
  }

  get blacklistConfig(): IBlacklistConfig {
    return this.data.blacklistConfig;
  }

  static async getKeyByLocationId(locationId): Promise<any> {
    return LocationsService.getKey(locationId)
  }

  static prepareDataFromFirestore(locationData: Dict): ILocation {
    return transformToCamelCase(locationData)
  }

  static getFromFirestoreModel(location: FirestoreLocation) {
    return new Location(this.prepareDataFromFirestore({...location.data, id: location.id}))
  }
}
